/**
 * Helpers de PRÉSENTATION du QA Meter (aucune logique de statut).
 *
 * La couleur d'un scénario / d'une page est calculée à la construction de
 * l'artefact (doc 03 §9.3) et arrive déjà décidée. Ici on ne fait que la
 * traduire en classe CSS + clé i18n, et formater les dates/versions.
 *
 * Ported from the donor `qa-meter.ui.ts`; type imports point to ./types,
 * i18n keys are unprefixed (no `qaMeter.` prefix), Vue-specific imports dropped.
 */
import type {
  QaAutomatedLane,
  QaAutomatedStatus,
  QaCaseStatus,
  QaColor,
  QaGrounding,
  QaHumanLane,
  QaPriority,
  QaTriColor,
} from './types';
import type { QaStringKey } from './i18n';

/** Classe modificatrice BEM appliquée aux pastilles/puces (cf. styles des composants). */
export const QA_COLOR_MODIFIER: Record<QaColor, string> = {
  green: 'qa--green',
  yellow: 'qa--yellow',
  red: 'qa--red',
  gray: 'qa--gray',
};

/** Clé i18n du libellé d'une couleur (référencée dynamiquement → présente dans les deux locales). */
export const QA_COLOR_LABEL_KEY: Record<QaColor, QaStringKey> = {
  green: 'color.green',
  yellow: 'color.yellow',
  red: 'color.red',
  gray: 'color.gray',
};

/** Clé i18n du libellé d'un statut automated. */
export const QA_AUTOMATED_LABEL_KEY: Record<QaAutomatedStatus, QaStringKey> = {
  passing: 'automated.passing',
  failing: 'automated.failing',
  flaky: 'automated.flaky',
  never_run: 'automated.neverRun',
};

/** Modificateur sémaphore d'un volet automated (le volet, pas le scénario). */
export const QA_AUTOMATED_MODIFIER: Record<QaAutomatedStatus, string> = {
  passing: 'qa--green',
  failing: 'qa--red',
  flaky: 'qa--yellow',
  never_run: 'qa--gray',
};

/**
 * Modificateur sémaphore d'un volet humain (dev/client) :
 *  - `rejected` → rouge ; non validé → gris ; validé mais périmé/contredit → jaune ; validé → vert.
 */
export function humanLaneModifier(lane: { validated: boolean; rejected?: boolean; stale: boolean }): string {
  if (lane.rejected) {
    return 'qa--red';
  }
  if (!lane.validated) {
    return 'qa--gray';
  }
  return lane.stale ? 'qa--yellow' : 'qa--green';
}

/** Modificateur d'une puce de priorité (P0 = critique → rouge ; décroît vers neutre). */
export const QA_PRIORITY_MODIFIER: Record<QaPriority, string> = {
  P0: 'qa-prio--p0',
  P1: 'qa-prio--p1',
  P2: 'qa-prio--p2',
  P3: 'qa-prio--p3',
};

/** Clé i18n du tag de statut d'un cas (couvert / manuel / bloqué / question produit). */
export const QA_CASE_STATUS_LABEL_KEY: Record<QaCaseStatus, QaStringKey> = {
  covered: 'case.covered',
  manual: 'case.manual',
  blocked: 'case.blocked',
  'open-question': 'case.openQuestion',
};

/** Clé i18n de l'ancrage de l'attendu (vérifié / à confirmer / décision produit). */
export const QA_GROUNDING_LABEL_KEY: Record<QaGrounding, QaStringKey> = {
  verified: 'grounding.verified',
  'confirm-live': 'grounding.confirmLive',
  'product-question': 'grounding.productQuestion',
};

// ── Voies code/bot/human (3 icônes indépendantes, couleur 3-états) ───────────

/** Voie « bot » (exécution automatisée) : passe → vert ; échec/instable → jaune ; jamais exécuté → gris. */
export function botTriColor(lane: QaAutomatedLane): QaTriColor {
  if (lane.status === 'passing') {
    return 'green';
  }
  if (lane.status === 'failing' || lane.status === 'flaky') {
    return 'yellow';
  }
  return 'gray';
}

/** Voie humaine (« code » = dev, « human » = client) : validé & frais → vert ; rejeté → jaune ; non validé/périmé → gris. */
export function humanTriColor(lane: QaHumanLane): QaTriColor {
  if (lane.rejected) {
    return 'yellow';
  }
  if (lane.validated) {
    return lane.stale ? 'gray' : 'green';
  }
  return 'gray';
}

/** Agrège une voie sur un ensemble de cas : un jaune → jaune ; sinon tout vert → vert ; sinon gris. */
export function aggregateTri(colors: ReadonlyArray<QaTriColor>): QaTriColor {
  if (colors.some((c) => c === 'yellow')) {
    return 'yellow';
  }
  if (colors.length > 0 && colors.every((c) => c === 'green')) {
    return 'green';
  }
  return 'gray';
}

/** Rang de gravité d'une couleur, pour résumer un groupe par son « pire » état. */
const QA_COLOR_RANK: Record<QaColor, number> = { red: 3, yellow: 2, green: 1, gray: 0 };

/** Couleur résumé d'un groupe : rouge domine ; sinon jaune ; sinon vert ; sinon gris. */
export function worstColor(colors: ReadonlyArray<QaColor>): QaColor {
  return colors.reduce<QaColor>((acc, c) => (QA_COLOR_RANK[c] > QA_COLOR_RANK[acc] ? c : acc), 'gray');
}

/** Priorité la plus haute (P0 < P1 < P2 < P3) parmi une liste, ou `undefined`. */
export function topPriority(priorities: ReadonlyArray<QaPriority | undefined>): QaPriority | undefined {
  return priorities.filter((p): p is QaPriority => Boolean(p)).sort()[0];
}

/**
 * Formate une estampille ISO en date+heure locale courte (fr-CA → AAAA-MM-JJ HH:MM).
 * Retourne `''` pour une valeur absente/invalide (l'appelant masque alors le segment).
 */
export function formatQaDate(iso: string | undefined | null): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('fr-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Version d'app = chaîne opaque (doc 03 §1.1, jamais parsée). Tronquée pour
 * l'affichage : un timestamp de build complet est illisible dans une puce.
 */
export function shortVersion(version: string | undefined | null): string {
  if (!version) {
    return '';
  }
  return version.length > 19 ? `${version.slice(0, 19)}…` : version;
}
